import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Upload, FileText, CheckCircle, AlertCircle, X, Plus, Trash2, FileSpreadsheet } from 'lucide-react';
import { MoujaInfo } from '../types';
export const UploadScreen: React.FC = () => {
  const {
    user
  } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submissionType, setSubmissionType] = useState<'new_khatian' | 'khatian_correction'>('new_khatian');
  const isUnicode = user?.role === 'zso';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emptyMoujaRow: MoujaInfo = {
    district: '',
    upazila: '',
    mouja: '',
    jlNumber: '',
    khatianStartNo: '',
    khatianEndNo: '',
    totalKhatian: '',
    totalSheet: '',
    remarks: ''
  };
  const [moujaDetails, setMoujaDetails] = useState<MoujaInfo[]>([{
    ...emptyMoujaRow
  }]);
  const [files, setFiles] = useState({
    khatianData: null as File | null,
    gadgetCopy: null as File | null,
    handoverLetter: null as File | null
  });
  const handleMoujaChange = (index: number, field: keyof MoujaInfo, value: string) => {
    const updatedDetails = [...moujaDetails];
    updatedDetails[index][field] = value;
    // Auto-calculate total khatian if start and end are numbers
    if (field === 'khatianStartNo' || field === 'khatianEndNo') {
      const start = parseInt(updatedDetails[index].khatianStartNo);
      const end = parseInt(updatedDetails[index].khatianEndNo);
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        updatedDetails[index].totalKhatian = (end - start + 1).toString();
      }
    }
    setMoujaDetails(updatedDetails);
  };
  const addMoujaRow = () => {
    setMoujaDetails([...moujaDetails, {
      ...emptyMoujaRow
    }]);
  };
  const removeMoujaRow = (index: number) => {
    if (moujaDetails.length > 1) {
      const updatedDetails = [...moujaDetails];
      updatedDetails.splice(index, 1);
      setMoujaDetails(updatedDetails);
    }
  };
  const handleImportXls = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Simulate reading XLS and populating rows
      setTimeout(() => {
        setMoujaDetails([{
          district: 'Dhaka',
          upazila: 'Savar',
          mouja: 'Aminbazar',
          jlNumber: '45',
          khatianStartNo: '1',
          khatianEndNo: '500',
          totalKhatian: '500',
          totalSheet: '50',
          remarks: 'Imported'
        }, {
          district: 'Dhaka',
          upazila: 'Savar',
          mouja: 'Bhakurta',
          jlNumber: '46',
          khatianStartNo: '501',
          khatianEndNo: '1200',
          totalKhatian: '700',
          totalSheet: '70',
          remarks: 'Imported'
        }]);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }, 500);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: keyof typeof files) => {
    if (e.target.files && e.target.files[0]) {
      setFiles({
        ...files,
        [type]: e.target.files[0]
      });
    }
  };
  const removeFile = (type: keyof typeof files) => {
    setFiles({
      ...files,
      [type]: null
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      // Reset form
      setMoujaDetails([{
        ...emptyMoujaRow
      }]);
      setFiles({
        khatianData: null,
        gadgetCopy: null,
        handoverLetter: null
      });
      setTimeout(() => setShowSuccess(false), 5000);
    }, 1500);
  };
  const FileUploadZone = ({
    title,
    type,
    accept,
    hint
  }: {
    title: string;
    type: keyof typeof files;
    accept: string;
    hint: string;
  }) => <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors relative" data-id="element-827">
      {files[type] ? <div className="flex flex-col items-center w-full" data-id="element-828">
          <div className="flex items-center justify-between w-full bg-white p-3 rounded border border-slate-200 shadow-sm" data-id="element-829">
            <div className="flex items-center" data-id="element-830">
              <FileText className="text-teal-600 mr-3" size={24} data-id="element-831" />
              <div className="flex flex-col overflow-hidden" data-id="element-832">
                <span className="text-sm font-medium text-slate-800 truncate max-w-[200px]" data-id="element-833">
                  {files[type]?.name}
                </span>
                <span className="text-xs text-slate-500" data-id="element-834">
                  {(files[type]?.size! / 1024).toFixed(1)} KB
                </span>
              </div>
            </div>
            <button type="button" onClick={() => removeFile(type)} className="text-slate-400 hover:text-red-500 p-1" data-id="element-835">
              <X size={18} data-id="element-836" />
            </button>
          </div>
        </div> : <>
          <Upload className="text-slate-400 mb-3" size={32} data-id="element-837" />
          <p className="text-sm font-medium text-slate-700 mb-1" data-id="element-838">{title}</p>
          <p className="text-xs text-slate-500 mb-4" data-id="element-839">{hint}</p>
          <label className="btn-secondary text-sm cursor-pointer" data-id="element-840">
            Browse File
            <input type="file" className="hidden" accept={accept} onChange={e => handleFileChange(e, type)} data-id="element-841" />
          </label>
        </>}
    </div>;
  return <div className="max-w-5xl mx-auto" data-id="element-842">
      <div className="mb-6 flex justify-between items-end" data-id="element-843">
        <div data-id="element-844">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-845">Data Submission</h1>
          <p className="text-slate-500" data-id="element-846">
            Upload khatian records and supporting documents
          </p>
        </div>
        <div className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-md text-sm font-medium border border-blue-200 flex items-center" data-id="element-847">
          <AlertCircle size={16} className="mr-2" data-id="element-848" />
          Format:{' '}
          {isUnicode ? 'Unicode (CSV/Excel)' : 'Non-Unicode (Bijoy/Legacy)'}
        </div>
      </div>

      {showSuccess && <div className="mb-6 bg-teal-50 border border-teal-200 text-teal-800 px-4 py-3 rounded-md flex items-center" data-id="element-849">
          <CheckCircle className="mr-3 text-teal-500" size={20} data-id="element-850" />
          <div data-id="element-851">
            <p className="font-medium" data-id="element-852">Submission Successful</p>
            <p className="text-sm text-teal-700" data-id="element-853">
              Your data has been forwarded to the Settlement Press Officer for
              validation.
            </p>
          </div>
        </div>}

      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-854">
        <div className="card p-6" data-id="element-855">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 border-b border-slate-100 pb-2" data-id="element-856">
            Submission Type
          </h2>
          <div className="flex space-x-6" data-id="element-857">
            <label className={`flex items-center space-x-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${submissionType === 'new_khatian' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-858">
              <input type="radio" name="submissionType" value="new_khatian" checked={submissionType === 'new_khatian'} onChange={() => setSubmissionType('new_khatian')} className="text-teal-600 focus:ring-teal-500" data-id="element-859" />
              <div data-id="element-860">
                <p className="text-sm font-medium text-slate-800" data-id="element-861">
                  New Khatian
                </p>
                <p className="text-xs text-slate-500" data-id="element-862">
                  Submit new khatian records
                </p>
              </div>
            </label>
            <label className={`flex items-center space-x-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${submissionType === 'khatian_correction' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-863">
              <input type="radio" name="submissionType" value="khatian_correction" checked={submissionType === 'khatian_correction'} onChange={() => setSubmissionType('khatian_correction')} className="text-amber-600 focus:ring-amber-500" data-id="element-864" />
              <div data-id="element-865">
                <p className="text-sm font-medium text-slate-800" data-id="element-866">
                  Khatian Correction
                </p>
                <p className="text-xs text-slate-500" data-id="element-867">
                  Submit correction for existing khatian
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="card p-0 overflow-hidden" data-id="element-868">
          <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-869">
            <h2 className="text-lg font-semibold text-slate-800" data-id="element-870">
              Mouja and Khatian Information
            </h2>
            <div data-id="element-871">
              <input type="file" accept=".xls,.xlsx" className="hidden" ref={fileInputRef} onChange={handleImportXls} data-id="element-872" />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-secondary text-sm flex items-center py-1.5 px-3" data-id="element-873">
                <FileSpreadsheet size={16} className="mr-2 text-green-600" data-id="element-874" />
                Import from XLS
              </button>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-875">
            <table className="min-w-full divide-y divide-slate-200" data-id="element-876">
              <thead className="bg-slate-100" data-id="element-877">
                <tr data-id="element-878">
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-879">
                    District
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-880">
                    Upazila
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-881">
                    Mouja
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-882">
                    JL No.
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-883">
                    Start No
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-884">
                    End No
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-885">
                    Total Khatian
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-886">
                    Total Sheet
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-887">
                    Remarks
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase" data-id="element-888">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200" data-id="element-889">
                {moujaDetails.map((row, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-890">
                    <td className="px-2 py-2" data-id="element-891">
                      <select required value={row.district} onChange={e => handleMoujaChange(index, 'district', e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-892">
                        <option value="" data-id="element-893">Select</option>
                        <option value="Dhaka" data-id="element-894">Dhaka</option>
                        <option value="Gazipur" data-id="element-895">Gazipur</option>
                        <option value="Narayanganj" data-id="element-896">Narayanganj</option>
                        <option value="Chittagong" data-id="element-897">Chittagong</option>
                      </select>
                    </td>
                    <td className="px-2 py-2" data-id="element-898">
                      <select required value={row.upazila} onChange={e => handleMoujaChange(index, 'upazila', e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-899">
                        <option value="" data-id="element-900">Select</option>
                        <option value="Savar" data-id="element-901">Savar</option>
                        <option value="Tongi" data-id="element-902">Tongi</option>
                        <option value="Rupganj" data-id="element-903">Rupganj</option>
                        <option value="Hathazari" data-id="element-904">Hathazari</option>
                      </select>
                    </td>
                    <td className="px-2 py-2" data-id="element-905">
                      <input type="text" required value={row.mouja} onChange={e => handleMoujaChange(index, 'mouja', e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="Mouja" data-id="element-906" />
                    </td>
                    <td className="px-2 py-2" data-id="element-907">
                      <input type="text" required value={row.jlNumber} onChange={e => handleMoujaChange(index, 'jlNumber', e.target.value)} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="JL" data-id="element-908" />
                    </td>
                    <td className="px-2 py-2" data-id="element-909">
                      <input type="text" required value={row.khatianStartNo} onChange={e => handleMoujaChange(index, 'khatianStartNo', e.target.value)} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="Start" data-id="element-910" />
                    </td>
                    <td className="px-2 py-2" data-id="element-911">
                      <input type="text" required value={row.khatianEndNo} onChange={e => handleMoujaChange(index, 'khatianEndNo', e.target.value)} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="End" data-id="element-912" />
                    </td>
                    <td className="px-2 py-2" data-id="element-913">
                      <input type="number" required value={row.totalKhatian} onChange={e => handleMoujaChange(index, 'totalKhatian', e.target.value)} className="w-24 border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="Total" data-id="element-914" />
                    </td>
                    <td className="px-2 py-2" data-id="element-915">
                      <input type="number" required value={row.totalSheet} onChange={e => handleMoujaChange(index, 'totalSheet', e.target.value)} className="w-24 border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="Sheets" data-id="element-916" />
                    </td>
                    <td className="px-2 py-2" data-id="element-917">
                      <input type="text" value={row.remarks} onChange={e => handleMoujaChange(index, 'remarks', e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-slate-900" placeholder="Remarks" data-id="element-918" />
                    </td>
                    <td className="px-2 py-2 text-center" data-id="element-919">
                      <button type="button" onClick={() => removeMoujaRow(index)} disabled={moujaDetails.length === 1} className={`p-1.5 rounded ${moujaDetails.length === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-red-500 hover:bg-red-50'}`} data-id="element-920">
                        <Trash2 size={16} data-id="element-921" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-slate-200 bg-slate-50" data-id="element-922">
            <button type="button" onClick={addMoujaRow} className="text-sm font-medium text-teal-600 hover:text-teal-800 flex items-center" data-id="element-923">
              <Plus size={16} className="mr-1" data-id="element-924" /> Add More Row
            </button>
          </div>
        </div>

        <div className="card p-6" data-id="element-925">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 border-b border-slate-100 pb-2" data-id="element-926">
            Document Uploads
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-927">
            <FileUploadZone title="Khatian Data File" type="khatianData" accept=".dat,.csv,.xlsx" hint={isUnicode ? 'Upload CSV/Excel file' : 'Upload legacy .dat file'} data-id="element-928" />
            <FileUploadZone title="Gadget Copy" type="gadgetCopy" accept=".pdf" hint="Upload scanned PDF" data-id="element-929" />
            <FileUploadZone title="Handover Letter" type="handoverLetter" accept=".pdf" hint="Upload signed PDF" data-id="element-930" />
          </div>
        </div>

        <div className="flex justify-end space-x-4" data-id="element-931">
          <button type="button" className="btn-secondary" data-id="element-932">
            Cancel
          </button>
          <button type="submit" className="btn-primary flex items-center" disabled={isSubmitting || !files.khatianData || !files.gadgetCopy || !files.handoverLetter} data-id="element-933">
            {isSubmitting ? <>Processing...</> : <>
                <Upload size={18} className="mr-2" data-id="element-934" />
                Submit for Validation
              </>}
          </button>
        </div>
      </form>
    </div>;
};